import { createContext, useContext } from 'react';

import { findHedgeProduct, getProduct } from '#/products/domain';
import { Greeks } from '#/unified-rfqs/entities/greeks';

import { UnifiedInstruments } from '#/unified-rfqs/ui/instruments/types';
import { exceptionLogger } from '@paradigm/logging/src/logging';
import { Maybe } from '@paradigm/utils/types';
import { futureLegView } from './views/future-leg';
import { optionLegView } from './views/option-leg';
import { uncategorizedLegView } from './views/uncategorized-leg';

import type { DraftLeg, FormOption, OptionKind } from './types';
import type { PriceView } from './views/leg-price';

interface BaseLegView {
  readonly index: number;

  readonly kind: 'OPTION' | 'FUTURE' | undefined;

  readonly isValid: boolean;
  readonly isRemovable: boolean;

  readonly greeks: Greeks | undefined | null;
  readonly isHedge: boolean;

  /** Not visible for grfq */
  readonly quantity: {
    readonly value: string | undefined;
    readonly contractQuantity: string;
    readonly isValid: boolean;
    readonly isVisible: boolean;
    readonly isDisabled: boolean;
  };
}

export interface FutureLegView extends BaseLegView {
  readonly kind: 'FUTURE';
  readonly isHedge: boolean;

  readonly product: {
    readonly options: readonly FormOption<string>[];
    readonly selectedOption: FormOption<string>;
    readonly isValid: true;
    readonly isDisabled: boolean;
  };

  readonly action: {
    readonly options: readonly string[];
    readonly selectedOption: 'BUY' | 'SELL';
    readonly isDisabled: boolean;
  };

  readonly instrumentKind: string;

  readonly expiration: {
    readonly options: readonly FormOption<number | null>[];
    readonly selectedOption: FormOption<number | null> | null;
    readonly isValid: boolean;
  };

  readonly price: PriceView;

  readonly ratio: {
    readonly value: string;
    readonly precision: number;
    readonly isValid: boolean;
    readonly isDisabled: boolean;
  };

  readonly instrument: string | undefined;
  readonly instrument_id: number | undefined;
}

export interface OptionLegView extends BaseLegView {
  readonly kind: 'OPTION';

  readonly product: {
    readonly options: readonly FormOption<string>[];
    readonly selectedOption: FormOption<string>;
    readonly isValid: true;
    readonly isDisabled: boolean;
  };

  readonly action: {
    readonly options: readonly string[];
    readonly selectedOption: 'BUY' | 'SELL';
    readonly isDisabled: boolean;
  };

  readonly instrumentKind: {
    readonly options: readonly FormOption<OptionKind>[];
    readonly selectedOption: FormOption<OptionKind> | null;
    readonly isValid: boolean;
    readonly isDisabled: boolean;
  };

  readonly expiration: {
    readonly options: readonly FormOption<number>[];
    readonly selectedOption: FormOption<number> | null;
    readonly isValid: boolean;
    readonly isDisabled: boolean;
  };

  readonly strike: {
    readonly options: readonly FormOption<string>[];
    readonly selectedOption: FormOption<string> | null;
    readonly isValid: boolean;
    readonly strikeIndex: number;
    readonly isDisabled: boolean;
  };

  readonly ratio: {
    readonly value: string;
    readonly precision: number;
    readonly isValid: boolean;
    readonly isDisabled: boolean;
  };

  readonly delta: string | undefined;
  readonly instrument: string | undefined;
  readonly instrument_id: number | undefined;
}

export interface UncategorizedLegView extends BaseLegView {
  readonly kind: undefined;

  readonly product: {
    readonly options: readonly FormOption<string>[];
    readonly selectedOption: null;
    readonly isValid: false;
    readonly isDisabled: boolean;
  };

  readonly isValid: false;
}

export type LegView = FutureLegView | OptionLegView | UncategorizedLegView;

export const LegViewCtx = createContext<LegView | null>(null);

LegViewCtx.displayName = 'RfqBuilderLegView';

export function useRfqBuilderLegView() {
  const ctx = useContext(LegViewCtx);
  if (ctx !== null) return ctx;
  throw new Error('Missing RfqBuilderProvider upwards in this tree.');
}

export function useRfqBuilderFutureLegView() {
  const view = useRfqBuilderLegView();
  if (view.kind !== 'FUTURE')
    throw new TypeError(`Leg should be an FUTURE leg but it's '${view.kind}'`);
  return view;
}

export function useRfqBuilderOptionLegView() {
  const view = useRfqBuilderLegView();
  if (view.kind !== 'OPTION')
    throw new TypeError(`Leg should be an OPTION leg but it's '${view.kind}'`);
  return view;
}

export function useRfqBuilderCategorizedLegView() {
  const view = useRfqBuilderLegView();
  if (view.kind === undefined)
    throw new TypeError(`Leg should be either FUTURE or OPTION`);
  return view;
}

export function createLegView(
  leg: DraftLeg,
  resources: UnifiedInstruments,
  selectedVenue: string,
  isCustomStrategy: boolean,
  isOrderBook: boolean,
): LegView {
  if (leg.kind === undefined)
    return uncategorizedLegView(
      leg,
      selectedVenue,
      isCustomStrategy,
      isOrderBook,
    );

  const { futureInstruments, optionInstruments } = resources;
  const product = getProduct(leg.productCode)!;

  if (leg.kind === 'FUTURE') {
    const productInstruments = filterInstrumentsByProduct(
      futureInstruments,
      product,
    );
    // Some products' perpetual is hedge-only (the option hedge IS the
    // perpetual), so it must not appear as a selectable expiration on a
    // standalone strategy leg. Those products carry
    // isPerpStrategyLegDisabled (e.g. Deribit SF/MA/RF). Standalone-
    // tradable perps (e.g. Paradex ETH-PERPETUAL) leave it unset and are
    // kept, and hedge legs always keep all instruments.
    const legInstruments =
      !leg.isHedge && product.isPerpStrategyLegDisabled
        ? productInstruments.filter((inst) => inst.expiration != null)
        : productInstruments;
    return futureLegView(leg, legInstruments, isCustomStrategy, isOrderBook);
  }

  let perpFutureInstrument: Maybe<
    UnifiedInstruments['futureInstruments'][number]
  > = null;
  try {
    const hedgeProduct = findHedgeProduct({ for: leg.productCode });
    const hedgeProductInstruments = filterInstrumentsByProduct(
      futureInstruments,
      hedgeProduct,
    );
    perpFutureInstrument = hedgeProductInstruments.find(
      (inst) => inst.expiration == null,
    );
  } catch (error) {
    exceptionLogger(`Failed to find hedge of product code ${leg.productCode}`)(
      error,
    );
  }
  return optionLegView(
    leg,
    filterInstrumentsByProduct(optionInstruments, product),
    perpFutureInstrument,
    isCustomStrategy,
    isOrderBook,
  );
}

function filterInstrumentsByProduct<T extends { product_code: string }>(
  instruments: readonly T[],
  product: ReturnType<typeof getProduct>,
) {
  return instruments.filter((ins) => ins.product_code === product.code);
}
